import {SIDE, TURN} from './env'
import {leftSide, rightSide, flipSide} from './navigation'
import {Vec2, Side} from './index.h'
import {Transition} from './transition'
import {LookaheadAlign} from './lookaheadAlign'
import {convertCellsToSides} from './convertCellsToSides'
import {convertSidesToTransitions} from './convertSidesToTransitions'
import {convertTransitionsToLookaheadAlign} from './convertTransitionsToLookaheadAlign'

type TrackSummary = {
  trackID: number
  cells: Vec2[]
  sides: Side[]
  transitions: Transition[]
  lookaheads: LookaheadAlign[]
}
type SideList = {
  side: Side
  in: Array<{
    trackID: number
    transition: Transition
    lookahead: LookaheadAlign
  }>
  out: Array<{
    trackID: number
    transition: Transition
    lookahead: LookaheadAlign
  }>
}
type CellSummary = {
  x: number
  y: number
  tracks: Array<{
    trackID: number
    side: Side
    transition: Transition
    lookahead: LookaheadAlign
  }>
  sideLists: {
    [K in Side]: SideList
  }
  locksMap: {
    [K in Side]: {
      in: number[]
      out: number[]
    }
  }
}
type CellSideOrder = {
  x: number
  y: number
  sideOrders: {
    [K in Side]: {
      in: Array<number | null>
      out: Array<number | null>
    }
  }
}
export type CellDrawConfig = {
  cellPadding: number
  directionPadding: number
  linePadding: number
  linesPerDirection: number
  roundPadding: number
}
export function compileCellList(
  tracks: Vec2[][],
  config?: Partial<CellDrawConfig>
) {
  const list = compileField(tracks)
  const cells = list.reduce(
    (acc, item) => [...acc, ...compileCell(item, config)],
    []
  )
  const trackTokenList = tracks.map((track, trackID) =>
    track.map(({x, y}) => {
      const result = cells.find(
        cell => cell.x === x && cell.y === y && cell.trackID === trackID
      )
      if (!result) throw Error('cell not found')
      return result
    })
  )
  const tokens: string[] = []
  for (const list of trackTokenList) {
    const result: string[] = []
    list.forEach(({tokens}, i) => {
      if (i === 0) return
      if (i === list.length - 1) return
      for (const {tag, props} of tokens) {
        if (tag === 'M') {
          if (i === 1) result.push(toDString(tag, ...props))
        } else {
          result.push(toDString(tag, ...props))
        }
      }
    })
    tokens.push(toDString(...result))
  }
  return tokens
  function toDString(...tokenList: (string | number)[]) {
    const resultList: string[] = []
    for (const token of tokenList) {
      if (token === ',' && resultList.length > 0) {
        resultList[resultList.length - 1] += ','
      } else {
        resultList.push(String(token))
      }
    }
    return resultList.join(' ')
  }
}
type DS = {
  x: number
  y: number
  trackID: number
  tokens: D[]
}
type SegmentRenderer = (opts: {
  trackID: number
  side: {in: Side; out: Side}
  order: {in: number; out: number}
}) => any
export function compileCell(
  {x, y, sideOrders}: CellSideOrder,
  {
    cellPadding = 18,
    directionPadding = 4,
    linePadding = 4,
    linesPerDirection = 6,
    roundPadding = 4
  }: Partial<CellDrawConfig> = {}
) {
  const cellSize =
    cellPadding * 2 + directionPadding + 2 * linesPerDirection * linePadding
  // console.log(cellSize)
  const cellOffset = {
    x: x * cellSize,
    y: y * cellSize
  }
  const nextCellOffset = {
    x: (x + 1) * cellSize,
    y: (y + 1) * cellSize
  }
  const ds: Array<DS> = []
  function addDS(trackID: number, tokens: D[]) {
    ds.push({
      x,
      y,
      trackID,
      tokens
    })
  }
  if (
    [
      sideOrders.UP.in,
      sideOrders.UP.out,
      sideOrders.RIGHT.in,
      sideOrders.RIGHT.out,
      sideOrders.DOWN.in,
      sideOrders.DOWN.out,
      sideOrders.LEFT.in,
      sideOrders.LEFT.out
    ].some(list => list.length > linesPerDirection)
  )
    throw Error('too many lines per direction')
  function lineOffsetFromStart(index) {
    return cellPadding + index * linePadding
  }
  function lineOffsetFromEnd(index) {
    return cellSize - lineOffsetFromStart(index)
  }
  const bendQ = 1
  function computeTrackSegment(
    side: Side,
    i: number,
    {
      straightLine,
      changeTrack,
      turnLeft,
      turnRight
    }: {
      straightLine: SegmentRenderer
      changeTrack: SegmentRenderer
      turnLeft: SegmentRenderer
      turnRight: SegmentRenderer
    }
  ) {
    const trackID = sideOrders[side].in[i]
    if (trackID === null || trackID === undefined) return
    const oppositeSide = sideOrders[flipSide(side)].out.indexOf(trackID)
    const leftTurnSide = sideOrders[leftSide(side)].out.indexOf(trackID)
    const rightTurnSide = sideOrders[rightSide(side)].out.indexOf(trackID)
    if (oppositeSide === -1 && leftTurnSide === -1 && rightTurnSide === -1)
      throw Error('backward movement')
    if (oppositeSide !== -1) {
      if (oppositeSide === i) {
        straightLine({
          trackID,
          side: {in: side, out: flipSide(side)},
          order: {in: i, out: oppositeSide}
        })
      } else {
        changeTrack({
          trackID,
          side: {in: side, out: flipSide(side)},
          order: {in: i, out: oppositeSide}
        })
      }
    } else {
      if (leftTurnSide !== -1) {
        turnLeft({
          trackID,
          side: {in: side, out: leftSide(side)},
          order: {in: i, out: leftTurnSide}
        })
      } else {
        turnRight({
          trackID,
          side: {in: side, out: rightSide(side)},
          order: {in: i, out: rightTurnSide}
        })
      }
    }
  }
  for (let i = 0; i < sideOrders.UP.in.length; i++) {
    computeTrackSegment(SIDE.UP, i, {
      straightLine({trackID, order}) {
        addDS(trackID, [
          d('M', cellOffset.x + lineOffsetFromStart(order.in), cellOffset.y),
          d(
            'L',
            cellOffset.x + lineOffsetFromStart(order.out),
            nextCellOffset.y
          )
        ])
      },
      changeTrack({trackID, order}) {
        const cellHalf = cellOffset.y + cellSize / 2
        const startX = lineOffsetFromStart(order.in)
        const endX = lineOffsetFromStart(order.out)
        const diffX = startX - endX
        const bendRadius = Math.abs(diffX / 2)
        const centerX = cellOffset.x + Math.max(startX, endX) - bendRadius
        addDS(trackID, [
          d('M', cellOffset.x + startX, cellOffset.y),
          d('L', cellOffset.x + startX, cellHalf - bendRadius * bendQ),
          d('Q', cellOffset.x + startX, cellHalf, ',', centerX, cellHalf),
          d(
            'Q',
            cellOffset.x + endX,
            cellHalf,
            ',',
            cellOffset.x + endX,
            cellHalf + bendRadius * bendQ
          ),
          d('L', cellOffset.x + endX, nextCellOffset.y)
        ])
      },
      turnLeft({trackID, order}) {
        const startX = cellOffset.x + lineOffsetFromStart(order.in)
        const endY = cellOffset.y + lineOffsetFromEnd(order.out)
        addDS(trackID, [
          d('M', startX, cellOffset.y),
          d('L', startX, endY - roundPadding),
          d('Q', startX, endY, ',', startX + roundPadding, endY),
          d('L', nextCellOffset.x, endY)
        ])
      },
      turnRight({trackID, order}) {
        const startX = cellOffset.x + lineOffsetFromStart(order.in)
        const endY = cellOffset.y + lineOffsetFromStart(order.out)
        addDS(trackID, [
          d('M', startX, cellOffset.y),
          d('L', startX, endY - roundPadding),
          d('Q', startX, endY, ',', startX - roundPadding, endY),
          d('L', cellOffset.x, endY)
        ])
      }
    })
  }
  for (let i = 0; i < sideOrders.RIGHT.in.length; i++) {
    const startX = nextCellOffset.x
    computeTrackSegment(SIDE.RIGHT, i, {
      straightLine({trackID, order}) {
        addDS(trackID, [
          d('M', startX, cellOffset.y + lineOffsetFromStart(order.in)),
          d('L', cellOffset.x, cellOffset.y + lineOffsetFromStart(order.out))
        ])
      },
      changeTrack({trackID, order}) {
        const cellHalf = cellOffset.x + cellSize / 2
        let startY = lineOffsetFromStart(order.in)
        let endY = lineOffsetFromStart(order.out)
        const diffY = startY - endY
        const bendRadius = Math.abs(diffY / 2)
        const centerY = cellOffset.y + Math.max(startY, endY) - bendRadius
        startY += cellOffset.y
        endY += cellOffset.y
        addDS(trackID, [
          d('M', startX, startY),
          d('L', cellHalf + bendRadius * bendQ, startY),
          d('Q', cellHalf, startY, ',', cellHalf, centerY),
          d('Q', cellHalf, endY, ',', cellHalf - bendRadius * bendQ, endY),
          d('L', cellOffset.x, endY)
        ])
      },
      turnLeft({trackID, order}) {
        const startY = cellOffset.y + lineOffsetFromStart(order.in)
        const endX = cellOffset.x + lineOffsetFromStart(order.out)
        addDS(trackID, [
          d('M', startX, startY),
          d('L', endX + roundPadding, startY),
          d('Q', endX, startY, ',', endX, startY + roundPadding),
          d('L', endX, nextCellOffset.y)
        ])
      },
      turnRight({trackID, order}) {
        const startY = cellOffset.y + lineOffsetFromStart(order.in)
        const endX = cellOffset.x + lineOffsetFromEnd(order.out)
        addDS(trackID, [
          d('M', startX, startY),
          d('L', endX + roundPadding, startY),
          d('Q', endX, startY, ',', endX, startY - roundPadding),
          d('L', endX, cellOffset.y)
        ])
      }
    })
  }
  for (let i = 0; i < sideOrders.DOWN.in.length; i++) {
    computeTrackSegment(SIDE.DOWN, i, {
      straightLine({trackID, order}) {
        addDS(trackID, [
          d('M', cellOffset.x + lineOffsetFromEnd(order.in), nextCellOffset.y),
          d('L', cellOffset.x + lineOffsetFromEnd(order.out), cellOffset.y)
        ])
      },
      changeTrack({trackID, order}) {
        const cellHalf = cellOffset.y + cellSize / 2
        const startX = lineOffsetFromEnd(order.in)
        const endX = lineOffsetFromEnd(order.out)
        const diffX = startX - endX
        const bendRadius = Math.abs(diffX / 2)
        const centerX = cellOffset.x + Math.max(startX, endX) - bendRadius
        addDS(trackID, [
          d('M', cellOffset.x + startX, nextCellOffset.y),
          d('L', cellOffset.x + startX, cellHalf + bendRadius * bendQ),
          d('Q', cellOffset.x + startX, cellHalf, ',', centerX, cellHalf),
          d(
            'Q',
            cellOffset.x + endX,
            cellHalf,
            ',',
            cellOffset.x + endX,
            cellHalf - bendRadius * bendQ
          ),
          d('L', cellOffset.x + endX, cellOffset.y)
        ])
      },
      turnLeft({trackID, order}) {
        const startX = cellOffset.x + lineOffsetFromEnd(order.in)
        const endY = cellOffset.y + lineOffsetFromStart(order.out)
        addDS(trackID, [
          d('M', startX, nextCellOffset.y),
          d('L', startX, endY + roundPadding),
          d('Q', startX, endY, ',', startX - roundPadding, endY),
          d('L', cellOffset.x, endY)
        ])
      },
      turnRight({trackID, order}) {
        const startX = cellOffset.x + lineOffsetFromEnd(order.in)
        const endY = cellOffset.y + lineOffsetFromEnd(order.out)
        addDS(trackID, [
          d('M', startX, nextCellOffset.y),
          d('L', startX, endY + roundPadding),
          d('Q', startX, endY, ',', startX + roundPadding, endY),
          d('L', nextCellOffset.x, endY)
        ])
      }
    })
  }
  for (let i = 0; i < sideOrders.LEFT.in.length; i++) {
    const startX = cellOffset.x
    computeTrackSegment(SIDE.LEFT, i, {
      straightLine({trackID, order}) {
        addDS(trackID, [
          d('M', startX, cellOffset.y + lineOffsetFromEnd(order.in)),
          d('L', nextCellOffset.x, cellOffset.y + lineOffsetFromEnd(order.out))
        ])
      },
      changeTrack({trackID, order}) {
        const cellHalf = cellOffset.x + cellSize / 2
        let startY = lineOffsetFromEnd(order.in)
        let endY = lineOffsetFromEnd(order.out)
        const diffY = startY - endY
        const bendRadius = Math.abs(diffY / 2)
        const centerY = cellOffset.y + Math.max(startY, endY) - bendRadius
        startY += cellOffset.y
        endY += cellOffset.y
        addDS(trackID, [
          d('M', startX, startY),
          d('L', cellHalf - bendRadius * bendQ, startY),
          d('Q', cellHalf, startY, ',', cellHalf, centerY),
          d('Q', cellHalf, endY, ',', cellHalf + bendRadius * bendQ, endY),
          d('L', nextCellOffset.x, endY)
        ])
      },
      turnLeft({trackID, order}) {
        const startY = cellOffset.y + lineOffsetFromEnd(order.in)
        const endX = cellOffset.x + lineOffsetFromEnd(order.out)
        addDS(trackID, [
          d('M', startX, startY),
          d('L', endX - roundPadding, startY),
          d('Q', endX, startY, ',', endX, startY - roundPadding),
          d('L', endX, cellOffset.y)
        ])
      },
      turnRight({trackID, order}) {
        const startY = cellOffset.y + lineOffsetFromEnd(order.in)
        const endX = cellOffset.x + lineOffsetFromStart(order.out)
        addDS(trackID, [
          d('M', startX, startY),
          d('L', endX - roundPadding, startY),
          d('Q', endX, startY, ',', endX, startY + roundPadding),
          d('L', endX, nextCellOffset.y)
        ])
      }
    })
  }
  return ds
  function d(tag, ...props) {
    return new D({tag, props})
  }
}
class D {
  tag: string
  props: (string | number)[]
  constructor({tag, props}: {tag: string; props: (string | number)[]}) {
    this.tag = tag
    this.props = props
  }
}
export function compileField(tracks: Vec2[][]): CellSideOrder[] {
  if (!tracks.every(track => track.length > 2)) throw Error('tracks too short')
  const trackSummary: Array<TrackSummary> = []
  for (let trackID = 0; trackID < tracks.length; trackID++) {
    const cells = tracks[trackID]
    //NOTE strong assumption: each track must start from left to right
    const sides = convertCellsToSides({x: cells[0].x - 1, y: cells[0].y}, cells)
    const transitions = convertSidesToTransitions(sides)
    const lookaheads = convertTransitionsToLookaheadAlign(transitions)
    trackSummary.push({
      trackID,
      cells,
      sides,
      transitions,
      lookaheads
    })
  }
  const cellSummary: Array<CellSummary> = []
  function getCellSummary(cell: Vec2): CellSummary {
    let result = cellSummary.find(
      item => item.x === cell.x && item.y === cell.y
    )
    if (!result) {
      result = {
        x: cell.x,
        y: cell.y,
        tracks: [],
        sideLists: {
          UP: {
            side: SIDE.UP,
            in: [],
            out: []
          },
          RIGHT: {
            side: SIDE.RIGHT,
            in: [],
            out: []
          },
          DOWN: {
            side: SIDE.DOWN,
            in: [],
            out: []
          },
          LEFT: {
            side: SIDE.LEFT,
            in: [],
            out: []
          }
        },
        locksMap: {
          UP: {in: [], out: []},
          RIGHT: {in: [], out: []},
          DOWN: {in: [], out: []},
          LEFT: {in: [], out: []}
        }
      }
      cellSummary.push(result)
    }
    return result
  }
  for (const {trackID, cells, sides, transitions, lookaheads} of trackSummary) {
    for (let i = 0; i < cells.length; i++) {
      const cell = getCellSummary(cells[i])
      cell.tracks.push({
        trackID,
        side: sides[i],
        transition: transitions[i],
        lookahead: lookaheads[i]
      })
    }
  }
  const sortRanks: {[K in Side]: [Side, Side, Side]} = {
    UP: [SIDE.LEFT, SIDE.DOWN, SIDE.RIGHT],
    RIGHT: [SIDE.UP, SIDE.LEFT, SIDE.DOWN],
    DOWN: [SIDE.LEFT, SIDE.UP, SIDE.RIGHT],
    LEFT: [SIDE.UP, SIDE.RIGHT, SIDE.DOWN]
  }
  const turnRanks = [TURN.LEFT, TURN.STOP, TURN.RIGHT]
  function sortIn(side: Side, a, b) {
    const turnAi = turnRanks.indexOf(a.lookahead.turn)
    const turnBi = turnRanks.indexOf(b.lookahead.turn)
    if (turnAi === -1 || turnBi === -1) throw Error('wrong turn')
    if (turnAi === turnBi) {
      switch (turnRanks[turnAi]) {
        case TURN.LEFT:
          return a.lookahead.distance - b.lookahead.distance
        case TURN.STOP:
          return 0
        case TURN.RIGHT:
          return b.lookahead.distance - a.lookahead.distance
        default:
          throw Error('wrong turn')
      }
    }
    return turnAi - turnBi
  }
  function sortOut(
    side: Side,
    directionQ: 1 | -1,
    a: {
      trackID: number
      transition: Transition
      lookahead: LookaheadAlign
    },
    b: {
      trackID: number
      transition: Transition
      lookahead: LookaheadAlign
    }
  ) {
    const axisQ = side === SIDE.RIGHT || side === SIDE.DOWN ? -1 : 1
    const turnAi = turnRanks.indexOf(a.lookahead.turn)
    const turnBi = turnRanks.indexOf(b.lookahead.turn)
    if (turnAi === -1 || turnBi === -1) throw Error('wrong turn')
    if (turnAi === turnBi) {
      switch (turnRanks[turnAi]) {
        case TURN.LEFT:
          return (a.lookahead.distance - b.lookahead.distance) * axisQ
        case TURN.STOP:
          return 0
        case TURN.RIGHT:
          return (b.lookahead.distance - a.lookahead.distance) * axisQ
        default:
          throw Error('wrong turn')
      }
    }
    return (turnAi - turnBi) * axisQ
  }
  const sideOrdersList: CellSideOrder[] = []
  function getCellTrackIndex(trackID: number, x, y) {
    const track = tracks[trackID]
    const currentCellIndex = track.findIndex(
      cell => cell.x === x && cell.y === y
    )
    if (currentCellIndex === -1) throw Error('cell not found')
    return currentCellIndex
  }
  function getDistanceFromCurrentCellToTrackEnd(trackID: number, x, y) {
    const track = tracks[trackID]
    return track.length - getCellTrackIndex(trackID, x, y)
  }
  function upsert<T>(list: T[], item: T) {
    if (list.includes(item)) return
    list.push(item)
  }
  cellSummary.sort((a, b) => {
    const aDist = a.tracks
      .map(({trackID}) =>
        getDistanceFromCurrentCellToTrackEnd(trackID, a.x, a.y)
      )
      .reduce((acc, dist) => acc + dist, 0)
    const bDist = b.tracks
      .map(({trackID}) =>
        getDistanceFromCurrentCellToTrackEnd(trackID, b.x, b.y)
      )
      .reduce((acc, dist) => acc + dist, 0)
    return aDist - bDist
  })
  for (const {tracks, sideLists, x, y, locksMap} of cellSummary) {
    const sideOrders: {
      [K in Side]: {
        in: Array<number | null>
        out: Array<number | null>
      }
    } = {
      UP: {in: [], out: []},
      RIGHT: {in: [], out: []},
      DOWN: {in: [], out: []},
      LEFT: {in: [], out: []}
    }
    for (const {trackID, transition, lookahead} of tracks) {
      sideLists[transition.from].in.push({
        trackID,
        transition,
        lookahead
      })
      sideLists[transition.to].out.push({
        trackID,
        transition,
        lookahead
      })
    }
    sideLists.UP.in.sort((a, b) => sortOut(SIDE.UP, -1, a, b))
    sideLists.RIGHT.in.sort((a, b) => sortOut(SIDE.RIGHT, -1, a, b))
    sideLists.DOWN.in.sort((a, b) => sortOut(SIDE.DOWN, -1, a, b))
    sideLists.LEFT.in.sort((a, b) => sortOut(SIDE.LEFT, -1, a, b))

    sideLists.UP.out.sort((a, b) => sortOut(SIDE.UP, 1, a, b))
    sideLists.RIGHT.out.sort((a, b) => sortOut(SIDE.RIGHT, 1, a, b))
    sideLists.DOWN.out.sort((a, b) => sortOut(SIDE.DOWN, 1, a, b))
    sideLists.LEFT.out.sort((a, b) => sortOut(SIDE.LEFT, 1, a, b))

    sideOrders.UP.in = sideLists.UP.in.map(({trackID}) => trackID)
    sideOrders.RIGHT.in = sideLists.RIGHT.in.map(({trackID}) => trackID)
    sideOrders.DOWN.in = sideLists.DOWN.in.map(({trackID}) => trackID)
    sideOrders.LEFT.in = sideLists.LEFT.in.map(({trackID}) => trackID)

    sideOrders.UP.out = sideLists.UP.out.map(({trackID}) => trackID)
    sideOrders.RIGHT.out = sideLists.RIGHT.out.map(({trackID}) => trackID)
    sideOrders.DOWN.out = sideLists.DOWN.out.map(({trackID}) => trackID)
    sideOrders.LEFT.out = sideLists.LEFT.out.map(({trackID}) => trackID)

    for (const {trackID, transition} of tracks) {
      const inIndex = sideOrders[transition.from].in.indexOf(trackID)
      const outIndex = sideOrders[transition.to].out.indexOf(trackID)
      if (inIndex === -1) throw Error('trackId not found')
      if (transition.turn === TURN.CENTER || transition.turn === TURN.STOP) {
        if (inIndex !== outIndex) {
          upsert(locksMap[transition.to].out, inIndex)
        }
      }
      if (transition.turn === TURN.LEFT) {
        const inOpposite = flipSide(transition.from)
        const outOpposite = flipSide(transition.to)
        upsert(locksMap[inOpposite].out, inIndex)
        upsert(locksMap[outOpposite].in, outIndex)
      }
    }
    setSideOrdersOut(SIDE.UP)
    setSideOrdersOut(SIDE.RIGHT)
    setSideOrdersOut(SIDE.DOWN)
    setSideOrdersOut(SIDE.LEFT)
    function setSideOrdersOut(side: Side) {
      let offset = 0
      for (let i = 0; i < sideLists[side].out.length; i++) {
        const {trackID} = sideLists[side].out[i]
        let index = i + offset
        while (locksMap[side].out.includes(index)) {
          sideOrders[side].out[index] = null
          offset += 1
          index = i + offset
        }
        sideOrders[side].out[index] = trackID
      }
      offset = 0
      for (let i = 0; i < sideLists[side].in.length; i++) {
        const {trackID} = sideLists[side].in[i]
        let index = i + offset
        while (locksMap[side].in.includes(index)) {
          sideOrders[side].in[index] = null
          offset += 1
          index = i + offset
        }
        sideOrders[side].in[index] = trackID
      }
    }

    sideOrdersList.push({x, y, sideOrders})
  }
  function findSideOrder({x, y}: Vec2) {
    const result = sideOrdersList.find(item => item.x === x && item.y === y)
    if (!result) throw Error('cell not found')
    return result.sideOrders
  }
  function findInSide(trackID, sideOrders) {
    const inSide: Side | null = sideOrders.UP.in.includes(trackID)
      ? SIDE.UP
      : sideOrders.RIGHT.in.includes(trackID)
      ? SIDE.RIGHT
      : sideOrders.DOWN.in.includes(trackID)
      ? SIDE.DOWN
      : sideOrders.LEFT.in.includes(trackID)
      ? SIDE.LEFT
      : null
    if (inSide === null) throw Error('track not found')
    return inSide
  }
  function findOutSide(trackID: number, sideOrders) {
    const outSide: Side | null = sideOrders.UP.out.includes(trackID)
      ? SIDE.UP
      : sideOrders.RIGHT.out.includes(trackID)
      ? SIDE.RIGHT
      : sideOrders.DOWN.out.includes(trackID)
      ? SIDE.DOWN
      : sideOrders.LEFT.out.includes(trackID)
      ? SIDE.LEFT
      : null
    if (outSide === null) throw Error('track not found')
    return outSide
  }
  function findInOrder(trackID, sideOrders): number {
    const inSide = findInSide(trackID, sideOrders)
    return sideOrders[inSide].in.indexOf(trackID)
  }
  function findOutOrder(trackID, sideOrders): number {
    const outSide = findOutSide(trackID, sideOrders)
    return sideOrders[outSide].out.indexOf(trackID)
  }
  const trackInSides = tracks.map((track, trackID) => {
    const firstCell = findSideOrder(track[0])
    const firstInSide = findInSide(trackID, firstCell)
    const results = [
      {
        inSide: firstInSide,
        inOrder: findOutOrder(trackID, firstCell),
        currentInOrder: firstCell[firstInSide].in.indexOf(trackID)
      }
    ]
    let prevOutOrder = findOutOrder(trackID, firstCell)
    for (let i = 1; i < track.length; i++) {
      const cell = track[i]
      const sideOrders = findSideOrder(cell)
      const inSide = findInSide(trackID, sideOrders)
      results.push({
        inSide,
        inOrder: prevOutOrder,
        currentInOrder: sideOrders[inSide].in.indexOf(trackID)
      })
      prevOutOrder = findOutOrder(trackID, sideOrders)
    }
    return {trackID, inSides: results}
  })
  for (let trackID = 0; trackID < tracks.length; trackID++) {
    const inSides_ = trackInSides.find(item => item.trackID === trackID)
    if (!inSides_) throw Error('track not found')
    const {inSides} = inSides_
    const track = tracks[trackID]
    for (let i = 0; i < track.length; i++) {
      const cell = track[i]
      const {inSide, inOrder, currentInOrder} = inSides[i]
      const sideOrders = findSideOrder(cell)
      const prevInOrder = sideOrders[inSide].in[currentInOrder]
      sideOrders[inSide].in[inOrder] = trackID
      if (inOrder !== currentInOrder) {
        if (prevInOrder === trackID) {
          sideOrders[inSide].in[currentInOrder] = null
        }
      }
    }
    last: {
      const i = track.length - 1
      const cell = track[i]
      const sideOrders = findSideOrder(cell)
      //NOTE strong assumption: each track must ends from left to right
      sideOrders.RIGHT.out = [...sideOrders.LEFT.in]
    }
  }
  function isInOrderInUse(order: number, sideOrders, ignoreTrackID = Infinity) {
    const trackID = sideOrders.in[order]
    return typeof trackID === 'number' && trackID !== ignoreTrackID
  }
  for (let trackID = 0; trackID < tracks.length; trackID++) {
    const track = tracks[trackID]
    const summary = trackSummary[trackID]
    // const cellSummaries = track.map(getCellSummary)
    const sideOrdersList = track.map(findSideOrder)
    const orderChanges = sideOrdersList.map(sideOrder => ({
      in: findInOrder(trackID, sideOrder),
      out: findOutOrder(trackID, sideOrder)
    }))
    for (let i = 0; i < track.length; i++) {
      // const cell = track[i]
      // const cellSummary = cellSummaries[i]
      const iSideOrders = sideOrdersList[i]
      const orderChange = orderChanges[i]
      const transition = summary.transitions[i]
      // const lookahead = summary.lookaheads[i]
      const straightBend =
        transition.turn === TURN.CENTER && orderChange.in !== orderChange.out
      const plainTurn = transition.turn !== TURN.CENTER
      if (straightBend || plainTurn) {
        const isStraight = transition.turn === TURN.CENTER
        const referenceOrder = isStraight ? orderChange.in : orderChange.out
        lookahead: for (let j = i + 1; j < track.length; j++) {
          const jTransition = summary.transitions[j]
          const jSideOrders = sideOrdersList[j]
          const jOrderChanges = orderChanges[j]
          if (
            isInOrderInUse(
              referenceOrder,
              jSideOrders[jTransition.from],
              trackID
            )
          ) {
            break lookahead
          }
          if (jTransition.turn !== TURN.CENTER) {
            iSideOrders[transition.to].out[orderChange.out] = null
            iSideOrders[transition.to].out[referenceOrder] = trackID
            orderChange.out = referenceOrder
            for (let k = i + 1; k < j; k++) {
              const kTransition = summary.transitions[k]
              const kOrderChanges = orderChanges[k]
              const kSideOrders = sideOrdersList[k]
              kSideOrders[kTransition.from].in[kOrderChanges.in] = null
              kSideOrders[kTransition.to].out[kOrderChanges.out] = null
              kSideOrders[kTransition.from].in[referenceOrder] = trackID
              kSideOrders[kTransition.to].out[referenceOrder] = trackID
              kOrderChanges.in = referenceOrder
              kOrderChanges.out = referenceOrder
            }
            jSideOrders[jTransition.from].in[jOrderChanges.in] = null
            jSideOrders[jTransition.from].in[referenceOrder] = trackID
            jOrderChanges.in = referenceOrder
            break lookahead
          }
        }
      }
    }
  }
  return sideOrdersList
}
